import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { logError } from '@cortex/faro';
import { config } from '../config.js';

// カスタムエラークラス
/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business UX Mirror APIクライアント。認証・Overview・Screens・Funnels・ServiceHealth・Users・Errorsの各エンドポイントを呼び出し
 * @graph-connects config [calls] API URL設定参照
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// エラーレスポンスの型
interface ErrorResponse {
  error?: {
    message?: string;
    code?: string;
  };
}

// 汎用フェッチラッパー
/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...Object.fromEntries(
      options.headers instanceof Headers
        ? options.headers.entries()
        : Object.entries(options.headers ?? {}),
    ),
  };

  const res = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    if (res.headers.get('X-Edge-Auth-Start')) {
      window.location.href = buildEdgeAuthStartUrl(window.location.href);
    }
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }

  if (!res.ok) {
    const err = (await res.json().catch((error: unknown) => {
      // エラーレスポンス本体が JSON でない場合は HTTP ステータスのみで報告する
      logError(error instanceof Error ? error : new Error(String(error)), {
        event: 'error_response_body_parse_failed',
      });
      return {} as ErrorResponse;
    })) as ErrorResponse;
    throw new ApiError(
      err.error?.message ?? `Request failed with status ${String(res.status)}`,
      res.status,
      err.error?.code,
    );
  }

  return res.json() as Promise<T>;
}

// ============================================================
// Auth API
// ============================================================

interface AuthUrlResponse {
  authUrl: string;
}

interface CurrentUserResponse {
  user: {
    email: string;
    name: string;
    picture: string;
  } | null;
  expiresAt: string | null;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export const authApi = {
  getAuthUrl: () => request<AuthUrlResponse>('/auth/login'),
  getCurrentUser: () => request<CurrentUserResponse>('/auth/me'),
  logout: () => request<undefined>('/auth/logout', { method: 'POST' }),
};

// ============================================================
// Overview API
// ============================================================

interface KpiDelta {
  value: number;
  previousValue: number;
  delta: number;
  deltaPercent: number | null;
}

interface KpiResponse {
  uniqueUsers: KpiDelta;
  uniqueSessions: KpiDelta;
  monthlyRegistrations: KpiDelta;
  purchases: KpiDelta;
  date: string;
  previousDate: string;
}

interface TrendPoint {
  date: string;
  uniqueUsers?: number;
  uniqueSessions?: number;
  monthlyRegistrations?: number;
  purchases?: number;
}

interface TrendsResponse {
  data: TrendPoint[];
  metrics: string[];
  startDate: string;
  endDate: string;
}

interface ScreenSummary {
  screen: string;
  eventCount: number;
  uniqueUsers: number;
  uniqueSessions: number;
  avgDwellTimeMs: number | null;
}

interface TopScreensResponse {
  data: ScreenSummary[];
  sortBy: string;
  limit: number;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export const overviewApi = {
  getKpi: (date?: string) => {
    const params = date ? `?date=${date}` : '';
    return request<KpiResponse>(`/v1/overview/kpi${params}`);
  },
  getTrends: (startDate: string, endDate: string, metrics: string[]) => {
    const params = new URLSearchParams({ startDate, endDate });
    for (const m of metrics) params.append('metrics', m);
    return request<TrendsResponse>(`/v1/overview/trends?${params.toString()}`);
  },
  getTopScreens: (startDate: string, endDate: string, limit = 10, sortBy = 'unique_users') => {
    const params = new URLSearchParams({
      startDate,
      endDate,
      limit: String(limit),
      sortBy,
    });
    return request<TopScreensResponse>(`/v1/overview/top-screens?${params.toString()}`);
  },
};

// ============================================================
// Screens API
// ============================================================

interface ScreenListResponse {
  data: {
    screen: string;
    service: string;
    eventCount: number;
    uniqueUsers: number;
    uniqueSessions: number;
    avgDwellTimeMs: number | null;
    rawScreenCount: number;
  }[];
  total: number;
}

interface ScreenMetricsResponse {
  screen: string;
  data: {
    date: string;
    eventCount: number;
    uniqueUsers: number;
    uniqueSessions: number;
    avgDwellTimeMs: number | null;
    p50DwellTimeMs: number | null;
    p90DwellTimeMs: number | null;
  }[];
}

interface ScreenEventsResponse {
  screen: string;
  data: {
    event: string;
    eventType: string;
    eventCount: number;
    uniqueUsers: number;
    uniqueSessions: number;
  }[];
}

interface ScreenDimensionsResponse {
  screen: string;
  dimensionName: string;
  data: {
    dimensionValue: string;
    eventCount: number;
    uniqueUsers: number;
    uniqueSessions: number;
  }[];
}

interface AvailableDimensionsResponse {
  screen: string;
  dimensions: string[];
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export const screensApi = {
  getList: (startDate: string, endDate: string, service?: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    if (service) params.set('service', service);
    return request<ScreenListResponse>(`/v1/screens?${params.toString()}`);
  },
  getAvailableDimensions: (name: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<AvailableDimensionsResponse>(
      `/v1/screens/${encodeURIComponent(name)}/available-dimensions?${params.toString()}`,
    );
  },
  getMetrics: (name: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<ScreenMetricsResponse>(
      `/v1/screens/${encodeURIComponent(name)}/metrics?${params.toString()}`,
    );
  },
  getEvents: (name: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<ScreenEventsResponse>(
      `/v1/screens/${encodeURIComponent(name)}/events?${params.toString()}`,
    );
  },
  getDimensions: (name: string, startDate: string, endDate: string, dimensionName: string) => {
    const params = new URLSearchParams({ startDate, endDate, dimensionName });
    return request<ScreenDimensionsResponse>(
      `/v1/screens/${encodeURIComponent(name)}/dimensions?${params.toString()}`,
    );
  },
};

// ============================================================
// Service Health API
// ============================================================

export interface PropertyRate {
  property: string;
  trueCount: number;
  falseCount: number;
  totalCount: number;
  trueRate: number;
}

export interface FeatureAvailability {
  feature: string;
  screen: string;
  path: string | null;
  dataAvailableFrom: string | null;
  properties: PropertyRate[];
  purchaseConversion: PurchaseConversion[];
}

interface AvailabilityTrendPoint {
  date: string;
  feature: string;
  property: string;
  trueRate: number;
}

export interface PurchaseConversion {
  purchaseType: string;
  events: number;
  users: number;
}

interface AvailabilityResponse {
  summary: FeatureAvailability[];
  trends: AvailabilityTrendPoint[];
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export const serviceHealthApi = {
  getAvailability: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<AvailabilityResponse>(`/v1/service-health/availability?${params.toString()}`);
  },
};

// ============================================================
// Funnels API
// ============================================================

export interface FunnelDefinition {
  id: string;
  name: string;
  description: string;
  steps: { name: string; screen: string; event?: string }[];
  dimensions: string[];
  analysisTypes: string[];
}

interface FunnelListResponse {
  data: FunnelDefinition[];
}

export interface FunnelDetailResponse {
  funnelId: string;
  funnelName: string;
  steps: {
    stepIndex: number;
    stepName: string;
    screen: string;
    event: string;
    uniqueUsers: number;
    dropoffRate: number | null;
    conversionRate: number;
    avgDwellTimeMs: number | null;
  }[];
  overallConversionRate: number | null;
  startDate: string;
  endDate: string;
}

interface FunnelDimensionsResponse {
  funnelId: string;
  dimensionName: string;
  data: {
    stepName: string;
    screen: string;
    event: string;
    dimensionValue: string;
    uniqueUsers: number;
  }[];
}

interface FunnelCompareResponse {
  funnelId: string;
  period1: { startDate: string; endDate: string };
  period2: { startDate: string; endDate: string };
  steps: {
    stepName: string;
    period1Users: number;
    period2Users: number;
    delta: number;
    deltaPercent: number | null;
  }[];
}

export interface FunnelDetailsResponse {
  funnelId: string;
  analysisType: string;
  data: Record<string, unknown>[];
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export const funnelsApi = {
  getList: () => request<FunnelListResponse>('/v1/funnels'),
  getDetail: (funnelId: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<FunnelDetailResponse>(`/v1/funnels/${funnelId}?${params.toString()}`);
  },
  getDimensions: (
    funnelId: string,
    startDate: string,
    endDate: string,
    dimensionName: string,
    step?: string,
  ) => {
    const params = new URLSearchParams({ startDate, endDate, dimensionName });
    if (step) params.set('step', step);
    return request<FunnelDimensionsResponse>(
      `/v1/funnels/${funnelId}/dimensions?${params.toString()}`,
    );
  },
  getCompare: (
    funnelId: string,
    period1Start: string,
    period1End: string,
    period2Start: string,
    period2End: string,
  ) => {
    const params = new URLSearchParams({
      period1Start,
      period1End,
      period2Start,
      period2End,
    });
    return request<FunnelCompareResponse>(`/v1/funnels/${funnelId}/compare?${params.toString()}`);
  },
  getDetails: (funnelId: string, startDate: string, endDate: string, analysisType: string) => {
    const params = new URLSearchParams({ startDate, endDate, analysisType });
    return request<FunnelDetailsResponse>(`/v1/funnels/${funnelId}/details?${params.toString()}`);
  },
};

// ============================================================
// Users API
// ============================================================

export interface UserEvent {
  eventId: string;
  timestamp: string;
  screen: string;
  event: string;
  eventType: 'screen_state' | 'user_action' | 'conversion' | 'error';
  properties: Record<string, unknown> | null;
  dwellTimeMs: number | null;
  pageUrl: string | null;
}

export interface UserSession {
  sessionId: string;
  startTime: string;
  endTime: string;
  deviceType: string;
  deviceCategory: string;
  osName: string;
  browserName: string;
  eventCount: number;
  events: UserEvent[];
}

interface UserTimelineResponse {
  user: { userId: string; internalId: number; visitorId: string | null };
  sessions: UserSession[];
  totalEvents: number;
  totalSessions: number;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export const usersApi = {
  getTimeline: (userId: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ userId, startDate, endDate });
    return request<UserTimelineResponse>(`/v1/users/timeline?${params.toString()}`);
  },
};

// ============================================================
// Errors API
// ============================================================

interface DailyRate {
  date: string;
  totalEvents: number;
  errorEvents: number;
  errorRate: number;
}

export interface AuthStatusBreakdown {
  authStatus: string;
  eventCount: number;
  uniqueSessions: number;
}

interface DailyRatesResponse {
  dailyRates: DailyRate[];
  summary: {
    totalErrors: number;
    avgErrorRate: number;
    sessionsAffected: number;
  };
  byAuthStatus: AuthStatusBreakdown[];
}

interface ErrorType {
  event: string;
  count: number;
  uniqueSessions: number;
}

export interface ErrorByScreen {
  screen: string;
  event: string;
  count: number;
  uniqueSessions: number;
  loggedInCount: number;
  anonymousCount: number;
}

interface BreakdownResponse {
  byType: ErrorType[];
  byScreen: ErrorByScreen[];
}

export interface ErrorMessage {
  errorMessage: string;
  event: string;
  screen: string;
  count: number;
  uniqueSessions: number;
  loggedInCount: number;
  anonymousCount: number;
}

interface MessagesResponse {
  messages: ErrorMessage[];
}

// ============================================================
// Styling API（型定義とクライアントは lib/styling-api.ts に分離）
// ============================================================

export {
  stylingApi,
  type SkuAnalysisItem,
  type UnderexposedSku,
  type FilterPattern,
  type StylistEvent,
} from './styling-api.js';

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-connects /api/ux-mirror [calls] /Api/Ux Mirrorを呼び出す
 */
export const errorsApi = {
  getDailyRates: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<DailyRatesResponse>(`/v1/errors/daily-rates?${params.toString()}`);
  },
  getBreakdown: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<BreakdownResponse>(`/v1/errors/breakdown?${params.toString()}`);
  },
  getMessages: (startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    return request<MessagesResponse>(`/v1/errors/messages?${params.toString()}`);
  },
};
